import API_CONFIG, { getHeaders, getApiUrl } from './config';

class OrderService {
  /**
   * Place a new order for the customer
   * @param {string} customerTicketId - The customer ticket ID
   * @param {Array} items - Array of items with menuItemId, quantity, specialInstructions
   * @returns {Promise<Object>} - Response with order details
   */
  async placeOrder(customerTicketId, items) {
    try {
      if (!customerTicketId) {
        throw new Error('Customer ticket ID is required');
      }

      if (!items || items.length === 0) {
        throw new Error('Order must contain at least one item');
      }

      const url = getApiUrl(API_CONFIG.ENDPOINTS.ORDERS.PLACE_ORDER);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: getHeaders(true),
        body: JSON.stringify({
          customerTicketId,
          items
        })
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.message || 'Failed to place order');
      }

      const data = responseData.data || responseData;

      return {
        success: true,
        data
      };
    } catch (error) {
      console.error('Place order error:', error);
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  }

  /**
   * Get the status of a specific order
   * @param {string} orderId - The order ID
   * @returns {Promise<Object>} - Response with order status and work tickets
   */
  async getOrderStatus(orderId) {
    try {
      if (!orderId) {
        throw new Error('Order ID is required');
      }

      const url = getApiUrl(API_CONFIG.ENDPOINTS.ORDERS.GET_STATUS(orderId));
      
      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(true)
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.message || 'Failed to fetch order status');
      }

      const data = responseData.data || responseData;

      return {
        success: true,
        data
      };
    } catch (error) {
      console.error('Get order status error:', error);
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  }

  /**
   * Generate bill for a customer ticket
   * @param {string} customerTicketId - The customer ticket ID
   * @returns {Promise<Object>} - Response with bill details
   */
  async generateBill(customerTicketId) {
    try {
      if (!customerTicketId) {
        throw new Error('Customer ticket ID is required');
      }

      const url = getApiUrl(API_CONFIG.ENDPOINTS.ORDERS.GENERATE_BILL);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: getHeaders(true),
        body: JSON.stringify({
          customerTicketId
        })
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.message || 'Failed to generate bill');
      }

      const data = responseData.data || responseData;

      return {
        success: true,
        data
      };
    } catch (error) {
      console.error('Generate bill error:', error);
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  }

  /**
   * Format cart items for order placement
   * @param {Array} cartItems - Cart items with id, quantity
   * @param {string} specialInstructions - General special instructions
   * @returns {Array} - Formatted items for API
   */
  formatCartItemsForOrder(cartItems, specialInstructions = '') {
    return cartItems.map(item => ({
      menuItemId: item.id,
      quantity: item.quantity,
      specialInstructions: specialInstructions || ''
    }));
  }

  /**
   * Get customer ticket ID from localStorage
   * @returns {string|null} - Customer ticket ID or null
   */
  getCustomerTicketId() {
    return localStorage.getItem('customerTicketId');
  }

  /**
   * Store customer ticket ID in localStorage
   * @param {string} ticketId - The customer ticket ID
   */
  setCustomerTicketId(ticketId) {
    if (ticketId) {
      localStorage.setItem('customerTicketId', ticketId);
    }
  }

  /**
   * Clear customer ticket ID from localStorage
   */
  clearCustomerTicketId() {
    localStorage.removeItem('customerTicketId');
  }

  /**
   * Poll order status at regular intervals
   * @param {string} orderId - The order ID to poll
   * @param {Function} onStatusUpdate - Callback when status updates
   * @param {number} intervalMs - Polling interval in milliseconds (default: 5000)
   * @returns {Function} - Function to stop polling
   */
  pollOrderStatus(orderId, onStatusUpdate, intervalMs = 5000) {
    const intervalId = setInterval(async () => {
      const result = await this.getOrderStatus(orderId);
      if (result.success) {
        onStatusUpdate(result.data);
        
        // Stop polling if order is resolved
        if (result.data.order.status === 'RESOLVED') {
          clearInterval(intervalId);
        }
      }
    }, intervalMs);

    // Return function to stop polling
    return () => clearInterval(intervalId);
  }

  /**
   * Get all orders for a customer ticket
   * @param {string} customerTicketId - The customer ticket ID
   * @returns {Promise<Object>} - Response with orders list
   */
  async getOrdersByCustomerTicket(customerTicketId) {
    try {
      if (!customerTicketId) {
        throw new Error('Customer ticket ID is required');
      }

      const url = getApiUrl(API_CONFIG.ENDPOINTS.ORDERS.GET_BY_CUSTOMER_TICKET(customerTicketId));
      
      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(true)
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.message || 'Failed to fetch orders');
      }

      const data = responseData.data || responseData;

      return {
        success: true,
        data
      };
    } catch (error) {
      console.error('Get orders by customer ticket error:', error);
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  }

  /**
   * Get the authenticated customer's previous customer tickets with their orders.
   * @returns {Promise<Object>} - Response grouped by customer ticket
   */
  async getCustomerTicketHistory() {
    try {
      const url = getApiUrl(API_CONFIG.ENDPOINTS.ORDERS.GET_CUSTOMER_TICKET_HISTORY);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(true)
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.message || 'Failed to fetch customer ticket history');
      }

      const data = responseData.data || responseData;

      return {
        success: true,
        data
      };
    } catch (error) {
      console.error('Get customer ticket history error:', error);
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  }
}

export default new OrderService();

